import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Clock, GraduationCap, FileText, AlertTriangle, ArrowRight,
  FolderOpen, ClipboardList, LayoutDashboard,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { employeeHubDashboard } from '../../../services/api';

const Card = ({ icon: Icon, label, value, hint, accent, to, testid }) => {
  const colors = {
    blue: 'from-blue-50 to-white border-blue-200 text-blue-700',
    emerald: 'from-emerald-50 to-white border-emerald-200 text-emerald-700',
    amber: 'from-amber-50 to-white border-amber-200 text-amber-700',
    rose: 'from-rose-50 to-white border-rose-200 text-rose-700',
    slate: 'from-slate-50 to-white border-slate-200 text-slate-700',
  }[accent] || 'from-slate-50 to-white border-slate-200 text-slate-700';
  const Inner = (
    <div className={`bg-gradient-to-br ${colors} border rounded-lg p-4 h-full flex flex-col`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <Icon className="h-5 w-5" />
        {to && <ArrowRight className="h-3.5 w-3.5 opacity-50" />}
      </div>
      <div className="text-xs uppercase tracking-wider text-slate-500 mt-3">{label}</div>
      <div className="text-2xl font-semibold text-slate-900 mt-0.5">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
  return to ? <Link to={to} className="block">{Inner}</Link> : Inner;
};

export default function EmployeeHub() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { setData(await employeeHubDashboard()); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Could not load hub'); }
      finally { setLoading(false); }
    })();
  }, []);

  const name = (user?.name || '').split(' ')[0] || 'there';
  const expiring = data?.expiringCerts || [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="employee-hub">
      <div className="flex items-center gap-2 text-slate-500 uppercase text-xs tracking-widest">
        <LayoutDashboard className="h-3.5 w-3.5" />Employee Hub
      </div>
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900">Hi {name}</h1>
        <p className="text-sm text-slate-500 mt-1">Your personal area — timesheets, training and documents in one place.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card icon={Clock} label="Hours this week" value={loading ? '…' : (data?.hoursThisWeek ?? 0).toFixed(1)}
          hint="Mon → Sun" accent="blue" to="/employee/time-clock" testid="hub-card-hours" />
        <Card icon={GraduationCap} label="Outstanding training" value={loading ? '…' : (data?.outstandingTraining ?? 0)}
          hint="Assigned but not complete" accent="amber" to="/employee/hub/training" testid="hub-card-training" />
        <Card icon={AlertTriangle} label="Expiring soon" value={loading ? '…' : expiring.length}
          hint="Within 60 days" accent={expiring.some(e => e.daysUntilDue < 0) ? 'rose' : 'amber'}
          to="/employee/hub/training" testid="hub-card-expiring" />
        <Card icon={FileText} label="Latest payslip" value={data?.latestPayslip ? '1' : '—'}
          hint={data?.latestPayslip ? data.latestPayslip.period : 'Nothing yet'} accent="slate" testid="hub-card-payslip" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg">
          <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-200">
            <h2 className="font-display uppercase tracking-wide text-sm sm:text-base">Expiring Soon</h2>
            <Link to="/employee/hub/training" className="text-xs font-medium text-blue-700 inline-flex items-center gap-1">
              Open My Training <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {loading && <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>}
            {!loading && expiring.length === 0 && (
              <div className="p-8 text-center text-slate-500 text-sm">All current — nothing expires in the next 60 days.</div>
            )}
            {expiring.map((e, i) => (
              <div key={i} className="p-4 sm:p-5 flex items-center justify-between gap-3" data-testid={`hub-expiring-${i}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{e.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {e.source === 'academy' ? 'Lutrom Academy' : 'External'} · {e.certNumber || '—'}
                  </div>
                </div>
                <div className={`shrink-0 text-xs font-medium px-2 py-1 rounded ${
                  e.daysUntilDue < 0 ? 'bg-rose-100 text-rose-800' :
                  e.daysUntilDue < 30 ? 'bg-amber-100 text-amber-800' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {e.daysUntilDue < 0 ? `${Math.abs(e.daysUntilDue)}d overdue` : `in ${e.daysUntilDue}d`}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <QuickLink to="/employee/hub/timesheets" icon={Clock} title="My Timesheets" hint={data?.openCorrections ? `${data.openCorrections} fix request${data.openCorrections > 1 ? 's' : ''} pending` : 'View your clocked hours · request fixes'} testid="hub-link-timesheets" />
          <QuickLink to="/employee/hub/training" icon={GraduationCap} title="My Training" hint="Academy + external certs" testid="hub-link-training" />
          <QuickLink to="/employee/hub/documents" icon={FolderOpen} title="My Documents" hint={data?.pendingAcknowledgements ? `${data.pendingAcknowledgements} awaiting acknowledgement` : 'Payslips, contracts, HR docs'} testid="hub-link-docs" />
          <QuickLink to="/employee/work-orders" icon={ClipboardList} title="My Work Orders" hint="Assigned to me" testid="hub-link-wo" />
        </div>
      </div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, title, hint, testid, disabled }) {
  const base = 'bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3 transition-colors';
  const classes = disabled
    ? `${base} opacity-50 cursor-not-allowed`
    : `${base} hover:border-slate-300 hover:bg-slate-50`;
  const inner = (
    <>
      <div className="h-9 w-9 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-slate-900 text-sm">{title}</div>
        <div className="text-xs text-slate-500 truncate">{hint}</div>
      </div>
      {!disabled && <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />}
    </>
  );
  return disabled
    ? <div className={classes} data-testid={testid}>{inner}</div>
    : <Link to={to} className={classes} data-testid={testid}>{inner}</Link>;
}
